import React from 'react'
import {appendComponent, linkState} from "../helpers/react-helpers"
import StripeChargeForm from "./StripeChargeForm"
import SaveCardForm from "./SaveCardForm"
import ManualChargeForm from "./ManualChargeForm"
import RadioGroup from './RadioGroup'
import RadioButton from './RadioButton'
import ActionButton from './ActionButton'
import {ErrorText, GridFormSingleSection, GridRowSingleCol, HtmlGridForm} from './grid-form'
import {FlexAuto, FlexRow} from './Flex'
import Icon from './Icon'
import icons from '../../markons/markons'
import useEvent from '../hooks/useEvent'
import ModalDialog from './ModalDialog'
import {FlexButtonBar} from './buttons'
import {CancelButton, SaveButton} from './SaveSubmitButton'
import {resolveRoute} from '../helpers/ajax'
import {useSaveHandler} from '../helpers/react-hooks'
import notify from '../notify'
import useGetServerData from '../hooks/useServerData'
import LoadingDots from './LoadingDots'
import kb from '../kb'
import {useStore} from '../helpers/react-form'
import AioText from './aio-inputs/AioText'
import {getCurrencyIcon} from '../data/currency-map'
import {parseFloat} from '../util/math'


type State = any;

type Props = {
    bookingId: string | number;
    customerId: string;
    testMode?: boolean;
    stripePublishableKey?: string;
    stripeUserId?: string;
};

interface RequestPaymentDialogProps {
    closeDialog: VoidFn
    bookingId: string | number;
}

interface SendPaymentRequestToCustomerResponse {
    customerEmail: string[]
    customerPhone: string
    emailSent: boolean
    smsSent: boolean
    smsError?: string
    emailError?: string
}

type Currency = {
    code: string
    decimal_places: number
}

type RequestPaymentDataResponse = {
    billing: Billing|null
    currency: Currency
    customer: {
        name: string
        cellPhone: string|null
        emails: string[]
    },
    twilioEnabled: boolean
}

type Billing = {
    base:                 LineItem[];
    items:                LineItem[];
    payments:             any[];
    grand_total:          string;
    grand_total_value:    string;
    total_payments:       string;
    total_payments_value: string;
    total_due:            string;
    total_due_value:      string;
    base_total_value:     string;
    error:                number;
}

const DEFAULT_RESPONSE = Object.freeze<RequestPaymentDataResponse>({
    billing: {
        base: [],
        items: [],
        payments: [],
        grand_total: '',
        grand_total_value: '',
        total_payments: '',
        total_payments_value: '',
        total_due: '',
        total_due_value: '',
        base_total_value: '',
        error: 0,
    },
    currency: {
        code: 'USD',
        decimal_places: 2,
    },
    customer: {
        name: '',
        cellPhone: null,
        emails: [],
    },
    twilioEnabled: false
})

type LineItem = {
    name:   string;
    amount: string;
}


function RequestPaymentDialog({closeDialog,bookingId}: RequestPaymentDialogProps) {
    const store = useStore({
        type: 'FULL',
        name: '',
        email: '',
        cellPhone: '',
        amount: '',
    })

    const {loading,data,error} = useGetServerData<RequestPaymentDataResponse>(['api.requestPayment',{booking_id:bookingId}],DEFAULT_RESPONSE, data => {
        if(data.customer) {
            if(data.customer.name) store.set('name',data.customer.name)
            if(data.customer.emails?.length) {
                store.set('email',data.customer.emails.join(', '))
            }
            if(data.customer.cellPhone) store.set('cellPhone',data.customer.cellPhone)
        }
    })

    const [requestPayment,sending] = useSaveHandler<SendPaymentRequestToCustomerResponse>({
        url: resolveRoute('sendPaymentRequestToCustomer',{id:bookingId}),
        data: () => store.data,
        errorText: "Failed to send payment request",
        success: res => {
            let message = '';

            if(res.data.emailSent) {
                if(res.data.customerEmail) {
                    message += `Email sent to ${res.data.customerEmail.join(', ')}.`;
                } else {
                    message += "Email sent.";
                }
            } else if(res.data.emailError) {
                if(res.data.customerEmail) {
                    message += `Error sending email to ${res.data.customerEmail.join(', ')}.`;
                } else {
                    message += "Email sending email.";
                }
            }
            message += ' ';
            if(res.data.smsSent) {
                if(res.data.customerPhone) {
                    message += `Text sent to ${res.data.customerPhone}.`;
                } else {
                    message += `Text sent.`;
                }
            } else if(res.data.smsError) {
                if(res.data.customerPhone) {
                    message += `Error sending text to ${res.data.customerPhone}.`;
                } else {
                    message += "Error sending text.";
                }
            }

            notify({
                text: message,
                className: res.data.emailError || res.data.smsError ? 'warning' : 'success',
            })
            closeDialog()
        }
    })

    if(loading) {
        return <LoadingDots height={140}/>
    } else if(error) {
        return <ErrorText>Failed to load payment information</ErrorText>
    }

    const totalDue = Number(data.billing?.total_due_value ?? 0)
    const grandTotal = Number(data.billing?.grand_total_value ?? 0)

    let requestAmount = 0

    if(store.data.type === 'FULL') {
        requestAmount = totalDue
    } else if(store.data.type === 'FIXED') {
        requestAmount = parseFloat(store.data.amount)
    } else if(store.data.type === 'PERCENT_DUE') {
        requestAmount = parseFloat(store.data.amount) / 100 * totalDue
    } else if(store.data.type === 'PERCENT_TOTAL') {
        requestAmount = parseFloat(store.data.amount) / 100 * grandTotal
    }

    const requestAmountFmt = kb.formatCurrencyIntl(requestAmount, data.currency.code)

    // const parts = new Intl.NumberFormat('en-US', {
    //     style: 'currency',
    //     currency: data.currency.code
    // }).formatToParts(requestAmount)
    // console.log(parts)

    return <>
        <GridFormSingleSection>
            <GridRowSingleCol>
                <AioText label="Name" {...store.input('name')}/>
            </GridRowSingleCol>
            <GridRowSingleCol>
                <AioText label="Email" {...store.input('email')}/>
            </GridRowSingleCol>
            <GridRowSingleCol>
                <AioText label="Cell Phone" {...store.input('cellPhone')}/>
            </GridRowSingleCol>
            <GridRowSingleCol>
                <RadioGroup {...store.input('type')}>
                    <RadioButton value="FULL">Total Due</RadioButton>
                    <RadioButton value="FIXED">Fixed Deposit</RadioButton>
                    <RadioButton value="PERCENT_DUE">% Due</RadioButton>
                    <RadioButton value="PERCENT_TOTAL">% Total</RadioButton>
                </RadioGroup>
            </GridRowSingleCol>
            {store.data.type !== 'FULL' ? <>
                <GridRowSingleCol>
                    <AioText label="Amount" inputMode="decimal" autoFocus
                             {...store.input('amount')}
                             icon={store.data.type.startsWith('PERCENT_') ? icons.faPercent : getCurrencyIcon(data.currency.code)}
                             width={100}/>
                </GridRowSingleCol>
            </> : null}
        </GridFormSingleSection>
        <FlexButtonBar marginTop="10px">
            <SaveButton icon={icons.faPaperPlane} onClick={requestPayment} processing={sending}>Request {requestAmountFmt}</SaveButton>
            <CancelButton onClick={closeDialog}>Cancel</CancelButton>
        </FlexButtonBar>
    </>
}


function SendPaymentRequestButton({bookingId}: {bookingId:StringNum}) {
    const openRequestPaymentDialog = useEvent(() => {
        appendComponent(({unmount}) => (
            <ModalDialog title="Request Payment" width="400px" onClose={unmount}>
                <RequestPaymentDialog bookingId={bookingId} closeDialog={unmount}/>
            </ModalDialog>
        ))
    })

    return <ActionButton className="btn-save" onClick={openRequestPaymentDialog}><Icon name={icons.faEnvelopeOpenDollar}/> Request Payment</ActionButton>
}

export default class RecordPaymentForm extends React.PureComponent<Props, State> {

    constructor(props: any) {
        super(props)
        this.state = {
            method: props.stripeUserId ? '' : 'MANUAL',
        }
    }

    render() {
        if (!this.props.stripeUserId) {
            return <ManualChargeForm bookingId={this.props.bookingId}/>
        }

        return (
            <>
                <HtmlGridForm>
                    <FlexRow gap={5}>
                        <FlexAuto>
                            <RadioGroup {...linkState.call(this, 'method')}>
                                <RadioButton value="CHARGE">Charge</RadioButton>
                                <RadioButton value="PREAUTH">Pre-Auth</RadioButton>
                                <RadioButton value="SAVE">Store Card</RadioButton>
                                <RadioButton value="MANUAL">Record Payment</RadioButton>
                            </RadioGroup>
                        </FlexAuto>

                        <FlexAuto>
                            <SendPaymentRequestButton bookingId={this.props.bookingId}/>
                        </FlexAuto>
                    </FlexRow>
                </HtmlGridForm>

                <div style={{marginTop: 10}}>
                    {this.renderBody()}
                </div>
            </>
        )
    }

    renderBody() {
        switch (this.state.method) {
            case 'CHARGE':
            case 'PREAUTH':
                return <StripeChargeForm bookingId={this.props.bookingId} stripeUserId={this.props.stripeUserId}
                                         stripePublishableKey={this.props.stripePublishableKey}
                                         testMode={this.props.testMode} capture={this.state.method === 'CHARGE'}/>
            case 'SAVE':
                return <SaveCardForm customerId={this.props.customerId}
                                     stripePublishableKey={this.props.stripePublishableKey} stripeUserId={this.props.stripeUserId}/>
            case 'MANUAL':
                return <ManualChargeForm bookingId={this.props.bookingId}/>
        }
    }
}
